import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { Field, field } from '../create/Field';

/**
 * The sales-tax rate on a document being written.
 *
 * There was no such field on any invoice screen. Both invoice paths sent nothing and the
 * server filled 0%, so a shop in a state with sales tax under-billed every job that didn't
 * come from a quote — silently, and unrecoverably once the customer had the paperwork.
 *
 * One component for every screen that writes money, because three separate tax inputs
 * would eventually disagree about what an empty box means. Here it means one thing: use
 * the shop's rate.
 */
export function TaxRateField({
  value,
  onChange,
  exempt,
}: {
  /** Percent as typed, e.g. "8.25". Empty means "whatever the shop charges". */
  value: string;
  onChange: (v: string) => void;
  /** The customer holds a certificate. The server enforces this; the screen explains it. */
  exempt?: boolean;
}) {
  const trpc = useTRPC();
  const org = useQuery(trpc.org.info.queryOptions());
  const shopRate = org.data?.defaultTaxRateBps;

  if (exempt) {
    // No input at all. Offering a box that the server will overrule is worse than saying
    // plainly why there's nothing to decide.
    return (
      <Field label="Sales tax">
        <p className="text-sm">
          <span className="badge badge--neutral">Tax exempt</span>
          <span className="ml-2 text-muted-foreground">
            This customer is marked exempt, so nothing is taxed.
          </span>
        </p>
      </Field>
    );
  }

  return (
    <Field label="Sales tax %">
      <input
        className={field}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="decimal"
        placeholder={
          shopRate === null || shopRate === undefined
            ? 'e.g. 8.25'
            : (shopRate / 100).toString()
        }
      />
      {/* Three different situations, three different sentences. A blank box that silently
          means 0% is the bug this whole field exists to end. */}
      {shopRate === null || shopRate === undefined ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Your shop has no sales-tax rate set, so this will bill at 0%.{' '}
          <Link
            to="/settings"
            className="font-semibold text-[var(--sw-brand-text)] hover:underline"
          >
            Set it once in Settings
          </Link>{' '}
          and it fills in from then on.
        </p>
      ) : value.trim() === '' ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Leave blank to use your shop rate ({(shopRate / 100).toString()}%).
        </p>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          Your shop rate is {(shopRate / 100).toString()}%. Clear this box to go back to
          it.
        </p>
      )}
    </Field>
  );
}

/**
 * Percent as typed → basis points, or `undefined` for "didn't say".
 *
 * `undefined` and 0 must stay different all the way to the server: an empty box means the
 * shop's rate applies, while a typed 0 is a decision (a resale, a job across a state line).
 * Collapsing them is exactly what `.default(0)` did in the schema, and why every invoice
 * went out untaxed.
 */
export function taxBps(percent: string): number | undefined {
  const trimmed = percent.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}
